/**
 * TOTP kurulumu — iki adımlı doğrulama.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * KURULUM İKİ ADIMDIR VE BU KASITLIDIR.
 *
 * `POST /auth/totp/setup` gizli anahtarı üretir ama TOTP'yi ETKİNLEŞTİRMEZ
 * (`totp_enabled_at` NULL kalır). Kullanıcı bir kod doğrulayana kadar giriş
 * akışı değişmez.
 *
 * Sebebi kilitlenmeyi önlemek: telefonu yanlış kurmuş bir kullanıcı, TOTP
 * hemen etkinleşseydi bir daha giremezdi. Sunucu bunu böyle tasarlamış;
 * arayüz de aynı sözleşmeyi görünür kılmalı — kullanıcı "henüz aktif değil,
 * doğrulayınca aktifleşecek" bilgisini görmeli.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { useState } from 'react';
import QRCode from 'qrcode';

import { Badge } from '../design/Status.tsx';
import { Button } from '../design/Button.tsx';
import { Card, CardBody, CardHeader } from '../design/Card.tsx';
import { Field, Input } from '../design/Field.tsx';
import { istek } from '../lib/api.ts';
import { useSessionRefresh } from './session.ts';

interface Kurulum {
  readonly secret: string;
  readonly otpauthUri: string;
}

export function TotpBolumu(): React.JSX.Element {
  const yenile = useSessionRefresh();
  const [kurulum, setKurulum] = useState<Kurulum | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [kod, setKod] = useState('');
  const [durum, setDurum] = useState<'bos' | 'kuruluyor' | 'dogrulaniyor'>('bos');
  const [hata, setHata] = useState<string | null>(null);
  const [etkin, setEtkin] = useState(false);

  const basla = (): void => {
    void (async () => {
      setDurum('kuruluyor');
      setHata(null);
      try {
        const k = await istek<Kurulum>('/auth/totp/setup', { method: 'POST' });
        setKurulum(k);
        // QR yerel üretilir — gizli anahtar hiçbir dış servise GİTMEZ.
        setQr(await QRCode.toDataURL(k.otpauthUri, { margin: 1, width: 220 }));
      } catch (error) {
        setHata(error instanceof Error ? error.message : 'Kurulum başlatılamadı.');
      } finally {
        setDurum('bos');
      }
    })();
  };

  const dogrula = (): void => {
    void (async () => {
      setDurum('dogrulaniyor');
      setHata(null);
      try {
        await istek('/auth/totp/enable', { method: 'POST', body: { code: kod } });
        setEtkin(true);
        setKurulum(null);
        setQr(null);
        setKod('');
        await yenile();
      } catch (error) {
        setHata(error instanceof Error ? error.message : 'Kod doğrulanamadı.');
      } finally {
        setDurum('bos');
      }
    })();
  };

  return (
    <Card>
      <CardHeader>
        <div>
          <p className="font-medium text-ink">İki adımlı doğrulama</p>
          <p className="text-sm text-ink-muted">İsteğe bağlı — hesabınızı güçlendirir</p>
        </div>
        {etkin && <Badge tone="ok">Etkin</Badge>}
      </CardHeader>
      <CardBody>
        {kurulum === null ? (
          <Button variant="secondary" loading={durum === 'kuruluyor'} onClick={basla}>
            {etkin ? 'Yeniden kur' : 'Kurulumu başlat'}
          </Button>
        ) : (
          <div className="flex flex-col gap-4">
            <Badge tone="warn">Henüz etkin değil — kodu doğrulayın</Badge>

            {qr !== null && (
              <img
                src={qr}
                width={220}
                height={220}
                alt="Kimlik doğrulama uygulamanızla taratılacak QR kodu"
                className="self-center rounded-[var(--radius-control)] border border-border"
              />
            )}

            <div>
              <p className="text-sm text-ink-muted">QR okutamıyorsanız bu anahtarı elle girin:</p>
              {/* `break-all` + tek aralıklı yazı: 32 karakterlik anahtar
                  telefonda okunabilir kalsın. */}
              <p className="mt-1 font-mono text-sm break-all text-ink">{kurulum.secret}</p>
            </div>

            <Field label="Uygulamadaki 6 haneli kod" required>
              {({ id, required }) => (
                <Input
                  id={id}
                  required={required}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={16}
                  value={kod}
                  onChange={(e) => setKod(e.target.value)}
                />
              )}
            </Field>

            <Button loading={durum === 'dogrulaniyor'} onClick={dogrula}>
              Doğrula ve etkinleştir
            </Button>
          </div>
        )}

        {hata !== null && (
          <p role="alert" className="mt-3 text-sm text-danger">
            {hata}
          </p>
        )}
      </CardBody>
    </Card>
  );
}
